import { recordOutreach } from '../../services/outreach';
import React, { useEffect, useRef, useState } from 'react';
import { useOwned } from './useCrm';
import { ref, get, update, push, set } from 'firebase/database';
import { database } from '../../firebaseConfig';
import { watchPath, formatPhoneDash, pauseInfo, optedOutTexts } from '../../services/crmService';
import { applyMerge } from './merge';
import { homeSearchSummary } from './homeSearch';
import { ui, MUTED } from './crmUi';

// ---- Call tallies for the signed-in user, counted from logged 'call' activities (crm/activity).
const startOf = (unit) => { const d = new Date(); d.setHours(0, 0, 0, 0); if (unit === 'week') d.setDate(d.getDate() - d.getDay()); if (unit === 'month') d.setDate(1); if (unit === 'year') { d.setMonth(0, 1); } return d.toISOString(); };
// Admins read all of crm/activity; everyone else reads the timelines of contacts they can see.
const loadActivity = (uid) => get(ref(database, 'crm/activity')).then((s) => s.val() || {}).catch(async () => {
  const ids = Object.keys((await get(ref(database, `crm/access/${uid}/contacts`))).val() || {});
  const rows = await Promise.all(ids.map((id) => get(ref(database, `crm/activity/${id}`)).then((x) => [id, x.val()]).catch(() => [id, null])));
  return Object.fromEntries(rows);
});
export function CallTallies({ uid, sessionCalls, sessionBad, refreshKey }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!uid) return;
    loadActivity(uid).then((all) => {
      const s = { val: () => all };
      const out = { today: 0, week: 0, month: 0, year: 0 }; const b = { today: startOf('day'), week: startOf('week'), month: startOf('month'), year: startOf('year') };
      Object.values(s.val() || {}).forEach((byId) => Object.values(byId || {}).forEach((e) => {
        if (!e || e.type !== 'call' || e.by !== uid || !e.at) return;
        Object.keys(out).forEach((k) => { if (e.at >= b[k]) out[k] += 1; });
      }));
      setT(out);
    }).catch(() => setT(null));
  }, [uid, refreshKey]);
  const cell = (label, n) => <div style={{ flex: '1 1 90px', textAlign: 'center', padding: 8, background: '#FBF7EC', border: '1px solid #d8c9a8', borderRadius: 8 }}><div style={{ fontSize: 20, fontWeight: 800 }}>{n ?? '-'}</div><div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div></div>;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '10px 0' }}>
      {cell('This session', sessionCalls)}{cell('Today', t?.today)}{cell('This week', t?.week)}{cell('This month', t?.month)}{cell('Year to date', t?.year)}{cell('Bad numbers removed', sessionBad)}
    </div>
  );
}

// ---- Session timer with an optional goal.
export function SessionTimer() {
  const [start] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  const [goal, setGoal] = useState('');
  const alerted = useRef(false);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const mins = (now - start) / 60000; const g = Number(goal) || 0; const pct = g ? Math.min(100, (mins / g) * 100) : 0;
  useEffect(() => { if (g && mins >= g && !alerted.current) { alerted.current = true; setTimeout(() => window.alert(`Nice work! You hit your ${g}-minute calling goal.`), 0); } if (!g || mins < g) alerted.current = mins >= g && !!g; }, [mins, g]);
  const mmss = `${Math.floor(mins)}:${String(Math.floor(((now - start) / 1000) % 60)).padStart(2, '0')}`;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 14 }}>
      <strong>Session {mmss}</strong>
      <label>Goal <input type="number" min="0" style={{ width: 60 }} value={goal} onChange={(e) => setGoal(e.target.value)} /> min</label>
      {g > 0 && <div style={{ flex: '1 1 160px', height: 10, background: '#eee', borderRadius: 5 }}><div style={{ width: `${pct}%`, height: 10, background: '#D4AF37', borderRadius: 5 }} /></div>}
      {g > 0 && mins >= g && <span style={{ color: '#1B5E20', fontWeight: 700 }}>Goal reached</span>}
    </div>
  );
}

// ---- Rich contact panel.
export function DialerContactPanel({ contact, pipeline }) {
  const [items, setItems] = useState([]);
  useEffect(() => watchPath(`crm/activity/${contact.id}`, (l) => setItems(l.sort((a, b) => String(b.at).localeCompare(String(a.at))))), [contact.id]);
  const last = items.find((e) => ['call', 'text', 'email'].includes(e.type));
  const phones = [contact.phone, contact.phone2, ...(contact.additionalPhones || [])].filter(Boolean);
  const addr = contact.address || [contact.street, contact.city, [contact.state, contact.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const status = contact.archived ? 'Archived' : pauseInfo(contact) ? pauseInfo(contact).label : 'Active';
  const row = (k, v) => (v ? <div><span style={{ color: MUTED, fontSize: 12, display: 'inline-block', width: 110 }}>{k}</span>{v}</div> : null);
  return (
    <div style={{ fontSize: 14, lineHeight: 1.6, margin: '8px 0', padding: 10, background: '#FAFAFA', border: '1px solid #eee', borderRadius: 8 }}>
      {row('Phone', phones.map(formatPhoneDash).join(', '))}
      {row('Email', contact.email)}
      {row('Address', addr)}
      {row('Type', (contact.contactTypes || []).join(', '))}
      {row('Source', contact.source)}
      {row('Status', status)}
      {row('Pipeline', (pipeline || []).map((p) => `${p.kindLabel}${p.status ? ` (${p.status})` : ''}`).join(', '))}
      {row('Looking for', homeSearchSummary(contact) || (pipeline || []).map((p) => (p.kindLabel === 'Buyer' ? [p.areas && [].concat(p.areas).join('/'), p.priceMax && `up to $${Number(p.priceMax).toLocaleString()}`].filter(Boolean).join(', ') : p.propertyAddress)).filter(Boolean).join('; '))}
      {row('Tags', (contact.tags || []).join(', '))}
      {row('Last contact', last ? new Date(last.at).toLocaleDateString('en-US', { timeZone: 'America/New_York' }) : 'Never')}
      {items.length > 0 && <div style={{ marginTop: 4 }}><span style={{ color: MUTED, fontSize: 12 }}>Recent</span>{items.slice(0, 3).map((e) => <div key={e.id} style={{ fontSize: 13 }}>{new Date(e.at).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}: {String(e.text || e.type).slice(0, 140)}</div>)}</div>}
    </div>
  );
}

// ---- Follow-up text after a disposition. Never sends by itself.
export const DEFAULT_TEXTS = [
  { id: 'd1', name: 'Sorry I missed you', body: "Hi {{first_name|there}}, it's {{agent_name}} with RESIDENCE | eXp Realty. Sorry I missed you! Is now a better time to chat?" },
  { id: 'd2', name: 'Great talking', body: 'Hi {{first_name|there}}, great talking with you today. Text me anytime with questions!' },
  { id: 'd3', name: 'Following up', body: "Hi {{first_name|there}}, just following up. Let me know if there's anything I can do for you." },
];
export const canText = (c) => !!c && !c.archived && !c.dnc && !optedOutTexts(c) && c.consentText === true && !!String(c.phone || '').replace(/\D/g, '');
export function FollowUpText({ contact, agent, isQuoUser, uid, onClose }) {
  const [tpls, setTpls] = useState([]);
  const [id, setId] = useState('');
  const [body, setBody] = useState('');
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const own_Tpls = useOwned('crm/textTemplates', { scoped: true });
  useEffect(() => setTpls(own_Tpls), [own_Tpls]);
  const all = [...tpls.filter((t) => t.body), ...DEFAULT_TEXTS];
  if (!canText(contact)) return <div style={{ fontSize: 13, color: MUTED }}>No follow-up text: {contact.archived ? 'archived' : contact.dnc ? 'On Do Not Call: no calls or texts' : optedOutTexts(contact) ? 'opted out of texts' : contact.consentText !== true ? 'no text consent on file' : 'no phone'}.</div>;
  const pick = (v) => { setId(v); const t = all.find((x) => x.id === v); setBody(t ? applyMerge(t.body, contact, agent) : ''); };
  const d = String(contact.phone).replace(/\D/g, '').slice(-10);
  const sendQuo = async () => {
    if (!window.confirm(`Send this text to ${formatPhoneDash(contact.phone)} from Quo?`)) return;
    setSending(true);
    try {
      const { auth } = await import('../../firebaseConfig'); const idToken = await auth.currentUser.getIdToken();
      const res = await fetch('/api/quo-send-sms', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }, body: JSON.stringify({ to: `+1${d}`, content: body, contactId: contact.id }) });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || `Error ${res.status}`);
      const r = push(ref(database, `crm/activity/${contact.id}`)); await set(r, { type: 'text', via: 'quo', notes: body, text: `Text sent via Quo: ${body}`, at: new Date().toISOString(), by: uid }); recordOutreach(uid, 'text', 'quo', { contactId: contact.id });
      setMsg(out.dryRun ? 'Test mode: not actually sent.' : 'Sent.');
    } catch (e) { setMsg(`Not sent: ${e.message}`); } finally { setSending(false); }
  };
  return (
    <div style={{ marginTop: 10, padding: 10, border: '1px solid #d8c9a8', borderRadius: 8 }}>
      <strong style={{ fontSize: 14 }}>Follow-up text</strong>
      <select style={{ ...ui.input, marginTop: 4 }} value={id} onChange={(e) => pick(e.target.value)}><option value="">Pick a text...</option>{all.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
      {id && <textarea style={{ ...ui.input, minHeight: 70 }} value={body} onChange={(e) => setBody(e.target.value)} />}
      {id && (isQuoUser
        ? <button style={ui.btn} disabled={sending || !body.trim()} onClick={sendQuo}>{sending ? 'Sending...' : 'Send text (Quo)'}</button>
        : <a style={{ ...ui.btn, textDecoration: 'none', display: 'inline-block' }} href={`sms:+1${d}?&body=${encodeURIComponent(body)}`}>Send text</a>)}
      {' '}<button style={ui.ghost} onClick={onClose}>Done</button>
      {msg && <div style={{ fontSize: 13, fontWeight: 700 }}>{msg}</div>}
    </div>
  );
}

// ---- Remove a bad number: only the dialed one; a secondary phone moves up if there is one.
export async function removeBadNumber(contact, agentName, uid) {
  const dialed = contact.phone;
  const extras = [contact.phone2, ...(contact.additionalPhones || [])].filter(Boolean);
  const patch = { phone: extras[0] || '', phone2: extras[1] || null, additionalPhones: extras.slice(2).length ? extras.slice(2) : null, badNumber: null, updatedAt: new Date().toISOString() };
  await update(ref(database, `crm/contacts/${contact.id}`), patch);
  const when = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' });
  const r = push(ref(database, `crm/activity/${contact.id}`));
  await set(r, { type: 'bad_number', removedPhone: String(dialed || ''), text: `Bad number removed: ${formatPhoneDash(dialed)} (Power Dialer, ${when}, ${agentName || 'agent'})`, at: new Date().toISOString(), by: uid });
}
